import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { urlFor, type BlogPost } from "@/lib/sanity";

type Props = {
  items: BlogPost[];
  eyebrow?: string;
  title?: string;
  description?: string;
};

function formatDate(iso?: string) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

export function BlogPostsSection({
  items,
  eyebrow = "Blog",
  title = "Field notes on agentic marketing",
  description,
}: Props) {
  if (!items || items.length === 0) return null;

  return (
    <section className="px-6 py-24">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-10">
          <SectionEyebrow>{eyebrow}</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance">
            {title}
          </h2>
          {description ? (
            <p className="mt-4 text-white/70 text-lg leading-relaxed">{description}</p>
          ) : null}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => {
            const slug = item.slug?.current;
            let Wrapper: React.ElementType = "div";
            let wrapperProps: Record<string, unknown> = {};
            if (slug) {
              Wrapper = Link;
              wrapperProps = { to: "/blog/$slug", params: { slug } };
            } else if (item.externalUrl) {
              Wrapper = "a";
              wrapperProps = {
                href: item.externalUrl,
                target: "_blank",
                rel: "noopener noreferrer",
              };
            }
            const isInternal = Wrapper === Link;
            const imgUrl = item.coverImage
              ? urlFor(item.coverImage).width(800).height(480).fit("crop").auto("format").url()
              : null;
            const date = formatDate(item.publishedAt);

            return (
              <Wrapper
                key={item._id}
                {...wrapperProps}
                className="group relative glass-card overflow-hidden hover-lift hover:-translate-y-1 hover:border-white/25 transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-[5/3] overflow-hidden bg-gradient-to-br from-purple-500/20 via-pink-500/10 to-transparent">
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={item.title}
                      loading="lazy"
                      className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div
                      aria-hidden
                      className="absolute inset-0"
                      style={{
                        background:
                          "radial-gradient(120% 80% at 20% 0%, rgba(217,70,239,0.35), transparent 60%), radial-gradient(120% 80% at 100% 100%, rgba(139,92,246,0.35), transparent 60%)",
                      }}
                    />
                  )}
                  {item.readTime ? (
                    <div className="absolute bottom-3 left-3 rounded-full bg-black/55 backdrop-blur-md border border-white/15 px-3 py-1 text-xs font-medium text-white">
                      {item.readTime}
                    </div>
                  ) : null}
                </div>

                <div className="relative p-6 flex flex-col gap-3 flex-1">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
                    {item.category ? <span className="text-pink-soft">{item.category}</span> : null}
                    {item.category && (item.author || date) ? (
                      <span className="text-white/30">•</span>
                    ) : null}
                    {item.author ? <span className="text-white/60">{item.author}</span> : null}
                    {item.author && date ? <span className="text-white/30">•</span> : null}
                    {date ? <span className="text-white/60">{date}</span> : null}
                  </div>

                  <h3 className="text-lg font-semibold text-white leading-snug text-balance group-hover:text-gradient transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-sm text-white/70 leading-relaxed line-clamp-3">
                    {item.excerpt}
                  </p>

                  {isInternal ? (
                    <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-sm text-pink-soft group-hover:text-white transition-colors">
                      Read article
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  ) : item.externalUrl ? (
                    <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-sm text-pink-soft group-hover:text-white transition-colors">
                      Read more
                      <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  ) : null}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
}
